import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { addHours, isValid } from "date-fns";
import { AxiosError } from "axios";
import type {
  BannerSwap,
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapResolution,
  ChannelSettings,
} from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import ChannelResultsPanel from "./ChannelResultsPanel";
import EditRunningSwapEndDateDialog from "./EditRunningSwapEndDateDialog";
import SwapChannelRemoveSection from "./SwapChannelRemoveSection";
import SwapOverlapResolutionDialog, {
  type OverlapCheckParams,
} from "../../BannerSwap/BannerSwapComponents/SwapOverlapResolutionDialog";
import {
  formatSwapDateCell,
  toBannerSwapDate,
  isPermanentSwapEnd,
} from "../bannerDashboardUtils";
import "../bannerSwapDialogs.css";

const ChannelsDetails: React.FC<{ rowData: BannerSwap }> = ({ rowData }) => {
  const [channelDetails, setChannelDetails] = useState<ChannelSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChannelDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const allSettings =
          await apiService.channelSettings.getAllChannelSettings();
        const idList = rowData.channelIDs.map(String);
        const seen = new Set<string>();
        const details: ChannelSettings[] = [];
        for (const id of idList) {
          const row = allSettings.find(
            (c) => c.ChannelId === id || (c as { id?: string }).id === id
          );
          if (row && !seen.has(row.ChannelId)) {
            seen.add(row.ChannelId);
            details.push(row);
          }
        }
        setChannelDetails(details);
      } catch (err) {
        console.error(err);
        setError("Failed to load channel details");
      } finally {
        setLoading(false);
      }
    };
    if (rowData.channelIDs?.length) {
      void fetchChannelDetails();
    } else {
      setLoading(false);
    }
  }, [rowData.channelIDs]);

  if (loading) {
    return (
      <p className="text-center text-gray-500 text-sm">Loading channels…</p>
    );
  }
  if (error) {
    return <p className="text-center text-red-500 text-sm">{error}</p>;
  }
  if (!channelDetails.length) {
    return (
      <p className="text-center text-gray-500 text-sm">No channel information</p>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <h2 className="text-xl font-semibold mb-3">Channel details</h2>
      <table className="table-auto w-full border-collapse text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="border border-gray-200 px-4 py-2 text-left">Name</th>
            <th className="border border-gray-200 px-4 py-2 text-left">Brand</th>
            <th className="border border-gray-200 px-4 py-2 text-left">Type</th>
            <th className="border border-gray-200 px-4 py-2 text-left">ID</th>
          </tr>
        </thead>
        <tbody>
          {channelDetails.map((ch, i) => (
            <tr key={ch.ChannelId} className={i % 2 ? "bg-gray-50" : ""}>
              <td className="border border-gray-200 px-4 py-2">
                {ch.ChannelTitle || "—"}
              </td>
              <td className="border border-gray-200 px-4 py-2">
                {Array.isArray(ch.Brand)
                  ? ch.Brand.join(", ")
                  : ch.Brand || "—"}
              </td>
              <td className="border border-gray-200 px-4 py-2">
                {ch.Type || "—"}
              </td>
              <td className="border border-gray-200 px-4 py-2 font-mono">
                {ch.ChannelId}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type Props = {
  rowData: BannerSwap;
  bannerSwapQueue: BannerSwap[];
  visible: boolean;
  onHide: () => void;
  onRefreshQueue: (swapUID?: string) => Promise<void>;
  onCancelSwap: (swapUID: string) => Promise<void>;
  cancelInProgress?: boolean;
  toast: React.RefObject<Toast | null>;
};

const SwapQueueDetailsDialog: React.FC<Props> = ({
  rowData,
  bannerSwapQueue,
  visible,
  onHide,
  onRefreshQueue,
  onCancelSwap,
  cancelInProgress = false,
  toast,
}) => {
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false);
  const [endDateEditOpen, setEndDateEditOpen] = useState(false);
  const [editStart, setEditStart] = useState<Date>(new Date());
  const [editEnd, setEditEnd] = useState<Date>(new Date());
  const [editPermanent, setEditPermanent] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
  const [overlapDialogData, setOverlapDialogData] =
    useState<BannerSwapOverlapAnalysis | null>(null);
  const [overlapCheckParams, setOverlapCheckParams] =
    useState<OverlapCheckParams | null>(null);
  const [overlapSubmitting, setOverlapSubmitting] = useState(false);

  const handleChannelRemoved = async (result: {
    swapCancelled: boolean;
    remainingChannelIds: string[];
  }) => {
    await onRefreshQueue(rowData.swapUID);
    if (result.swapCancelled || result.remainingChannelIds.length === 0) {
      setEndDateEditOpen(false);
      setScheduleEditOpen(false);
      onHide();
    }
  };

  const openScheduleEditor = () => {
    if (rowData.status !== "scheduled") {
      return;
    }
    setScheduleError(null);
    const startD = toBannerSwapDate(rowData.startDate);
    const endD = toBannerSwapDate(rowData.endDate);
    if (!isValid(startD) || !isValid(endD)) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid dates",
        detail: "Could not read this swap's schedule. Refresh the queue.",
      });
      return;
    }
    const permanent = isPermanentSwapEnd(endD);
    setEditStart(new Date(startD.getTime()));
    setEditPermanent(permanent);
    setEditEnd(
      permanent ? addHours(startD, 1) : new Date(endD.getTime())
    );
    setScheduleEditOpen(true);
  };

  const saveSchedule = async (
    overlapResolution?: BannerSwapOverlapResolution
  ) => {
    if (rowData.status !== "scheduled") {
      setScheduleError("Only scheduled swaps can be rescheduled.");
      return;
    }
    if (!isValid(editStart)) {
      setScheduleError("Start date is invalid.");
      return;
    }
    const endEffective = editPermanent
      ? new Date("9999-12-31T23:59:59.999Z")
      : editEnd;
    if (!editPermanent && !isValid(editEnd)) {
      setScheduleError("End date is invalid.");
      return;
    }
    if (editStart.getTime() >= endEffective.getTime()) {
      setScheduleError("End must be after start.");
      return;
    }
    setScheduleSaving(true);
    setScheduleError(null);
    try {
      await bannerDashboardApi.patchBannerSwapSchedule(
        rowData.swapUID,
        editStart,
        endEffective,
        overlapResolution
      );
      toast.current?.show({
        severity: "success",
        summary: "Schedule updated",
      });
      setScheduleEditOpen(false);
      setOverlapDialogOpen(false);
      setOverlapDialogData(null);
      setOverlapCheckParams(null);
      await onRefreshQueue(rowData.swapUID);
    } catch (err) {
      const ax = err as AxiosError<{
        message?: string;
        overlap?: BannerSwapOverlapAnalysis;
      }>;
      if (
        ax.response?.status === 409 &&
        ax.response.data?.overlap &&
        !overlapResolution
      ) {
        setOverlapDialogData(ax.response.data.overlap);
        setOverlapCheckParams({
          channelIDs: rowData.channelIDs,
          startDate: editStart,
          endDate: endEffective,
          excludeSwapUID: rowData.swapUID,
        });
        setOverlapDialogOpen(true);
        return;
      }
      const msg = ax.response?.data?.message ?? "Failed to update schedule.";
      setScheduleError(msg);
      toast.current?.show({ severity: "error", summary: "Update failed", detail: msg });
    } finally {
      setScheduleSaving(false);
    }
  };

  const confirmScheduleSave = async () => {
    if (rowData.status !== "scheduled") {
      setScheduleError("Only scheduled swaps can be rescheduled.");
      return;
    }
    if (!isValid(editStart)) {
      setScheduleError("Start date is invalid.");
      return;
    }
    const endEffective = editPermanent
      ? new Date("9999-12-31T23:59:59.999Z")
      : editEnd;
    if (!editPermanent && !isValid(editEnd)) {
      setScheduleError("End date is invalid.");
      return;
    }
    if (editStart.getTime() >= endEffective.getTime()) {
      setScheduleError("End must be after start.");
      return;
    }

    setScheduleSaving(true);
    setScheduleError(null);
    try {
      const overlap = await bannerDashboardApi.postBannerSwapOverlapCheck({
        channelIDs: rowData.channelIDs,
        startDate: editStart,
        endDate: endEffective,
        excludeSwapUID: rowData.swapUID,
      });
      if (overlap.hasConflicts) {
        setOverlapDialogData(overlap);
        setOverlapCheckParams({
          channelIDs: rowData.channelIDs,
          startDate: editStart,
          endDate: endEffective,
          excludeSwapUID: rowData.swapUID,
        });
        setOverlapDialogOpen(true);
        return;
      }
      await saveSchedule();
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      const msg = ax.response?.data?.message ?? "Failed to check overlaps.";
      setScheduleError(msg);
      toast.current?.show({ severity: "error", summary: "Update failed", detail: msg });
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleScheduleOverlapConfirm = async (
    resolution: BannerSwapOverlapResolution
  ) => {
    setOverlapSubmitting(true);
    try {
      await saveSchedule(resolution);
    } finally {
      setOverlapSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        header={
          <div className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 mb-1">
              <span className="font-mono text-base text-gray-600">
                #{rowData.swapUID}
              </span>
              <Button
                type="button"
                icon="pi pi-copy"
                rounded
                text
                size="small"
                aria-label="Copy swap UID"
                title="Copy swap UID"
                onClick={() => {
                  void navigator.clipboard?.writeText(String(rowData.swapUID));
                }}
              />
            </div>
            <h3 className="text-2xl m-0">{rowData.swapName}</h3>
          </div>
        }
        className="banner-swap-details-dialog"
        visible={visible}
        onHide={onHide}
        closable
      >
        <div className="flex flex-wrap gap-2 items-center pb-3 mb-4 border-b border-gray-200">
          {rowData.status === "scheduled" && (
            <Button
              label="Edit schedule"
              icon="pi pi-calendar"
              onClick={openScheduleEditor}
              style={{ backgroundColor: "#ff6b35", color: "#fff" }}
            />
          )}
          {rowData.status === "running" && (
            <Button
              label="Edit end date"
              icon="pi pi-calendar-times"
              onClick={() => setEndDateEditOpen(true)}
              style={{ backgroundColor: "#ff6b35", color: "#fff" }}
            />
          )}
          {rowData.status === "scheduled" && (
            <Button
              label="Cancel swap"
              style={{ backgroundColor: "#C41E3D", color: "#fff" }}
              loading={cancelSubmitting || cancelInProgress}
              disabled={cancelSubmitting || cancelInProgress}
              onClick={() => {
                void (async () => {
                  setCancelSubmitting(true);
                  try {
                    await onCancelSwap(rowData.swapUID);
                  } catch {
                    // Error toast handled by parent hook.
                  } finally {
                    setCancelSubmitting(false);
                  }
                })();
              }}
            />
          )}
        </div>

        <div className="flex gap-6 mb-6 items-start flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <table className="table-auto w-full border-collapse text-sm">
              <tbody>
                <tr className="bg-gray-50">
                  <td className="border px-4 py-2 font-medium w-1/3">Status</td>
                  <td className="border px-4 py-2 capitalize">{rowData.status}</td>
                </tr>
                <tr>
                  <td className="border px-4 py-2 font-medium">Start</td>
                  <td className="border px-4 py-2">
                    {formatSwapDateCell(rowData.startDate)}
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border px-4 py-2 font-medium">End</td>
                  <td className="border px-4 py-2">
                    {formatSwapDateCell(rowData.endDate)}
                  </td>
                </tr>
                <tr>
                  <td className="border px-4 py-2 font-medium">User</td>
                  <td className="border px-4 py-2">{rowData.createdBy}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border px-4 py-2 font-medium">Channels</td>
                  <td className="border px-4 py-2">{rowData.channelIDs.length}</td>
                </tr>
                <tr>
                  <td className="border px-4 py-2 font-medium">Description</td>
                  <td className="border px-4 py-2">{rowData.description}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-72 flex-shrink-0">
            <img
              src={rowData.channelBannerURL}
              alt="Swap banner"
              className="max-w-full rounded-lg shadow-md"
              style={{ maxHeight: "180px" }}
            />
          </div>
        </div>

        <div className="mb-6">
          <ChannelResultsPanel channelResults={rowData.channelResults} />
        </div>

        <ChannelsDetails rowData={rowData} />
      </Dialog>

      <EditRunningSwapEndDateDialog
        rowData={rowData}
        bannerSwapQueue={bannerSwapQueue}
        visible={endDateEditOpen}
        onHide={() => setEndDateEditOpen(false)}
        onSaved={async () => {
          setEndDateEditOpen(false);
          await onRefreshQueue(rowData.swapUID);
          onHide();
        }}
        toast={toast}
      />

      <Dialog
        header="Edit schedule"
        visible={scheduleEditOpen}
        style={{ width: "min(640px, 92vw)" }}
        onHide={() => !scheduleSaving && setScheduleEditOpen(false)}
        closable={!scheduleSaving}
      >
        <div className="flex flex-col gap-4 p-4">
          <Calendar
            value={isValid(editStart) ? editStart : null}
            onChange={(e) => {
              const v = e.value;
              if (v instanceof Date && isValid(v)) {
                setEditStart(new Date(v.getTime()));
              }
            }}
            showTime
            hourFormat="24"
            showIcon
            className="w-full"
          />
          <div className="flex items-center gap-2">
            <Checkbox
              inputId="editPermanentV2"
              checked={editPermanent}
              onChange={(e) => setEditPermanent(!!e.checked)}
            />
            <label htmlFor="editPermanentV2">Permanent end</label>
          </div>
          {!editPermanent && (
            <Calendar
              value={isValid(editEnd) ? editEnd : null}
              onChange={(e) => {
                const v = e.value;
                if (v instanceof Date && isValid(v)) {
                  setEditEnd(new Date(v.getTime()));
                }
              }}
              showTime
              hourFormat="24"
              showIcon
              className="w-full"
              minDate={isValid(editStart) ? editStart : undefined}
            />
          )}
          {scheduleError && (
            <p className="text-sm text-red-600 m-0">{scheduleError}</p>
          )}
          <SwapChannelRemoveSection
            swap={rowData}
            mode="scheduled"
            disabled={scheduleSaving}
            toast={toast}
            onChannelRemoved={handleChannelRemoved}
          />
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              disabled={scheduleSaving}
              onClick={() => setScheduleEditOpen(false)}
            />
            <Button
              label="Save"
              loading={scheduleSaving}
              onClick={() => void confirmScheduleSave()}
            />
          </div>
        </div>
      </Dialog>

      {overlapDialogData && overlapCheckParams ? (
        <SwapOverlapResolutionDialog
          visible={overlapDialogOpen}
          onHide={() => {
            if (overlapSubmitting) {
              return;
            }
            setOverlapDialogOpen(false);
            setOverlapDialogData(null);
            setOverlapCheckParams(null);
          }}
          overlap={overlapDialogData}
          overlapCheckParams={overlapCheckParams}
          onConfirm={handleScheduleOverlapConfirm}
          submitting={overlapSubmitting}
          confirmLabel="Confirm and save"
        />
      ) : null}
    </>
  );
};

export default SwapQueueDetailsDialog;
